var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { Turnstile } from '../constants';
import { createTurnstileEventTracker, shouldPollTurnstileToken, pollTurnstileToken, turnstileEnabled } from '../turnstile-helper';
var TurnstileContext = createContext(null);
export var useTurnstile = function () {
    var context = useContext(TurnstileContext);
    if (!context) {
        throw new Error('useTurnstile must be used within a TurnstileProvider');
    }
    return context;
};
export var TurnstileProvider = function (_a) {
    var children = _a.children, actionSource = _a.actionSource;
    var turnstileData = useRef({
        token: null,
        tokenExpired: false,
        errorCode: null,
        loaded: false,
        scriptLoadFailed: false,
        timing: {
            scriptLoadStartTime: null,
            scriptLoadEndTime: null,
            scriptLoadDuration: null,
            formSubmitTime: null,
            challengeStartTime: null,
            challengeEndTime: null,
            challengeDuration: null,
            submitToChallengeEndDuration: null,
        },
        pollTimeoutId: null,
    });
    var _b = useState(false), showTurnstileSpinner = _b[0], setShowTurnstileSpinner = _b[1];
    var turnstileEventTracker = createTurnstileEventTracker(turnstileData, actionSource);
    useEffect(function () {
        window.debugTurnstileStates = turnstileData.current;
    }, [turnstileData]);
    var onPollComplete = function (checkSuspectedSignup) {
        if (!window.turnstile) {
            setShowTurnstileSpinner(false);
            turnstileEventTracker({
                event: Turnstile.EVENTS.SCRIPT_NOT_LOADED,
                props: {
                    validatedUserType: Turnstile.USER_TYPES.PARENT,
                    turnstile_source: Turnstile.EVENT_PROPS.CTA_CLICK
                }
            });
            return { token: null, failure: true };
        }
        var token = turnstileData.current.token;
        if (!token) {
            setShowTurnstileSpinner(false);
            turnstileEventTracker({
                event: Turnstile.EVENTS.VALIDATION_FAILED,
                props: {
                    validatedUserType: Turnstile.USER_TYPES.PARENT,
                    error_code: turnstileData.current.errorCode,
                    turnstile_source: Turnstile.EVENT_PROPS.CTA_CLICK
                }
            });
            return { token: null, failure: true };
        }
        turnstileEventTracker({
            event: Turnstile.EVENTS.VALIDATION_SUCCESS,
            props: {
                validatedUserType: Turnstile.USER_TYPES.PARENT,
                turnstile_source: Turnstile.EVENT_PROPS.CTA_CLICK,
                suspected_popup_shown: checkSuspectedSignup()
            }
        });
        return { token: token, failure: false };
    };
    var onPollTimeout = function () {
        setShowTurnstileSpinner(false);
        if (!window.turnstile) {
            turnstileEventTracker({
                event: Turnstile.EVENTS.SCRIPT_NOT_LOADED,
                props: {
                    validatedUserType: Turnstile.USER_TYPES.PARENT,
                    turnstile_source: Turnstile.EVENT_PROPS.TIMEOUT
                }
            });
            return { token: null, failure: true };
        }
        if (!turnstileData.current.token) {
            turnstileEventTracker({
                event: Turnstile.EVENTS.VALIDATION_FAILED,
                props: {
                    validatedUserType: Turnstile.USER_TYPES.PARENT,
                    error_code: turnstileData.current.tokenExpired ? Turnstile.ERROR_CODES.TOKEN_EXPIRED : turnstileData.current.errorCode,
                    turnstile_source: Turnstile.EVENT_PROPS.TIMEOUT
                }
            });
            return { token: null, failure: true };
        }
        turnstileEventTracker({
            event: Turnstile.EVENTS.VALIDATION_SUCCESS,
            props: {
                validatedUserType: Turnstile.USER_TYPES.PARENT,
                turnstile_source: Turnstile.EVENT_PROPS.TIMEOUT
            }
        });
        return { token: turnstileData.current.token, failure: false };
    };
    var pollForToken = function (checkSuspectedSignup) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve) {
                    pollTurnstileToken(function () { return shouldPollTurnstileToken(turnstileData); }, 500, 6000, function () { return resolve(onPollComplete(checkSuspectedSignup)); }, function () { return resolve(onPollTimeout()); }, setShowTurnstileSpinner, turnstileData);
                })];
        });
    }); };
    var handleTurnstileSubmit = function (checkSuspectedSignup) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    turnstileData.current.timing.formSubmitTime = Date.now();
                    turnstileData.current.timing.submitToChallengeEndDuration = turnstileData.current.timing.challengeEndTime
                        ? (turnstileData.current.timing.challengeEndTime - turnstileData.current.timing.formSubmitTime) / 1000
                        : null;
                    if (!turnstileEnabled()) {
                        return [2 /*return*/, { token: null, failure: false }];
                    }
                    return [4 /*yield*/, pollForToken(checkSuspectedSignup)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    }); };
    var clearPollTimeout = function () {
        if (turnstileData.current.pollTimeoutId) {
            clearTimeout(turnstileData.current.pollTimeoutId);
        }
    };
    var displayTurnstileSpinner = function (show) {
        turnstileEnabled() && setShowTurnstileSpinner(show);
    };
    var turnstileSpinnerActive = function () {
        return turnstileEnabled() && showTurnstileSpinner;
    };
    var getTurnstileToken = function () {
        if (!turnstileEnabled()) {
            return null;
        }
        return turnstileData.current.token;
    };
    return (React.createElement(TurnstileContext.Provider, { value: {
            turnstileData: turnstileData,
            handleTurnstileSubmit: handleTurnstileSubmit,
            turnstileEventTracker: turnstileEventTracker,
            clearPollTimeout: clearPollTimeout,
            displayTurnstileSpinner: displayTurnstileSpinner,
            turnstileSpinnerActive: turnstileSpinnerActive,
            getTurnstileToken: getTurnstileToken
        } }, children));
};
